import { z } from 'zod';
import { FULFILMENT_STATUSES, ORDER_CHANNELS } from '../channels.js';
import { dateString, noteText, optionalGstin, optionalPhone, percent, periodString, phone, quantity, rupees, shortText, } from './common.js';
/* ---------------- point of sale ---------------- */
export const ORDER_TYPES = ['DINE_IN', 'TAKEAWAY', 'DELIVERY', 'ROOM_SERVICE'];
export const orderTypeEnum = z.enum(ORDER_TYPES);
/** ONLINE is money an aggregator or web checkout collected and owes us. */
export const PAYMENT_MODES = ['CASH', 'CARD', 'UPI', 'ROOM', 'ONLINE'];
export const paymentModeEnum = z.enum(PAYMENT_MODES);
export const createTicketSchema = z
    .object({
    type: orderTypeEnum,
    tableId: z.string().uuid().optional(),
    stayId: z.string().uuid().optional(),
    covers: z.number().int().min(1).max(50).default(1),
    note: noteText,
})
    .refine((v) => v.type !== 'ROOM_SERVICE' || Boolean(v.stayId), {
    message: 'Room service needs an in-house stay',
    path: ['stayId'],
})
    .refine((v) => v.type !== 'DINE_IN' || Boolean(v.tableId), {
    message: 'Dine-in needs a table',
    path: ['tableId'],
});
export const addTicketLineSchema = z.object({
    menuItemId: z.string().uuid(),
    quantity: z
        .number()
        .min(-100)
        .max(100)
        .refine((v) => v !== 0, 'Quantity must not be zero'),
    note: noteText,
});
export const billTicketSchema = z.object({
    mode: paymentModeEnum,
    /**
     * A bill discount, as a percentage of what is left to pay.
     *
     * The counter thinks in percent - "ten percent off" - and a percentage stays
     * right when a line is added after it was typed, where a rupee amount does
     * not. `discount` is still accepted for anything that already computes an
     * amount, and the percentage wins when both arrive.
     */
    discountPct: z.number().min(0).max(100).optional(),
    discount: rupees.default(0),
    discountReason: noteText,
    guestName: shortText.optional(),
    guestGstin: optionalGstin,
    guestStateCode: z
        .string()
        .regex(/^\d{2}$/)
        .optional(),
});
/**
 * Why a ticket was voided.
 *
 * A list rather than a free-text box: a counter voiding a ticket in front of a
 * waiting guest types whatever is quickest, and "asd" tells the owner reading
 * the audit trail nothing. These are the reasons an Indian restaurant actually
 * voids for, and anything genuinely different can still be typed.
 */
export const VOID_REASONS = [
    'Guest cancelled',
    'Ordered by mistake',
    'Wrong item punched',
    'Item not available',
    'Kitchen delay',
    'Duplicate ticket',
    'Guest left without ordering',
    'Test ticket',
];
export const voidTicketSchema = z.object({
    reason: shortText,
});
export const voidInvoiceSchema = z.object({
    reason: shortText,
});
/* ---------------- kitchen display ---------------- */
export const KDS_STATES = ['QUEUED', 'PREPARING', 'READY', 'SERVED'];
export const kdsStateEnum = z.enum(KDS_STATES);
export const bumpKdsSchema = z.object({
    state: kdsStateEnum,
});
/* ---------------- restaurant tables & reservations ---------------- */
export const createTableSchema = z.object({
    code: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9-]{1,8}$/),
    seats: z.number().int().min(1).max(30),
    section: shortText.default('Main'),
});
export const RESERVATION_STATUSES = [
    'BOOKED',
    'CONFIRMED',
    'SEATED',
    'COMPLETED',
    'CANCELLED',
    'NO_SHOW',
];
export const createReservationSchema = z.object({
    guestName: shortText,
    phone,
    email: z.string().trim().toLowerCase().email().optional().or(z.literal('')),
    partySize: z.number().int().min(1).max(50),
    date: dateString,
    time: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:MM'),
    durationMinutes: z.number().int().min(30).max(360).default(90),
    tableId: z.string().uuid().optional(),
    note: noteText,
});
export const updateReservationSchema = z.object({
    status: z.enum(RESERVATION_STATUSES).optional(),
    tableId: z.string().uuid().nullable().optional(),
    partySize: z.number().int().min(1).max(50).optional(),
    date: dateString.optional(),
    time: z
        .string()
        .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
        .optional(),
    note: noteText,
});
/* ---------------- rooms & front desk ---------------- */
export const ROOM_STATUSES = ['VACANT', 'OCCUPIED', 'DIRTY', 'OUT_OF_ORDER'];
export const roomStatusEnum = z.enum(ROOM_STATUSES);
export const createRoomSchema = z.object({
    number: z.string().trim().max(10).min(1),
    roomTypeId: z.string().uuid(),
    floor: z.number().int().min(0).max(60),
    status: roomStatusEnum.default('VACANT'),
});
export const createRoomTypeSchema = z.object({
    name: shortText,
    baseTariff: rupees,
    maxOccupancy: z.number().int().min(1).max(10),
    amenities: z.array(shortText).max(20).default([]),
});
export const roomStatusSchema = z.object({
    status: roomStatusEnum,
    note: noteText,
});
export const checkInSchema = z.object({
    guest: z.object({
        id: z.string().uuid().optional(),
        name: shortText,
        phone: optionalPhone,
        email: z.string().trim().toLowerCase().email().optional().or(z.literal('')),
        idType: z.enum(['AADHAAR', 'PASSPORT', 'DRIVING_LICENCE', 'VOTER_ID']).default('AADHAAR'),
        idNumber: z.string().trim().max(30).optional().or(z.literal('')),
        address: z.string().trim().max(240).optional().or(z.literal('')),
        gstin: optionalGstin,
        stateCode: z
            .string()
            .regex(/^\d{2}$/)
            .optional(),
    }),
    nights: z.number().int().min(1).max(365),
    adults: z.number().int().min(1).max(10),
    children: z.number().int().min(0).max(10).default(0),
    tariff: rupees.optional(),
    advance: rupees.default(0),
    reservationId: z.string().uuid().optional(),
    /** The online booking this arrival is against, if there was one. */
    bookingId: z.string().uuid().optional(),
});
export const updateStaySchema = z.object({
    nights: z.number().int().min(1).max(365).optional(),
    tariff: rupees.optional(),
    advance: rupees.optional(),
});
export const checkOutSchema = z.object({
    mode: z.enum(['CASH', 'CARD', 'UPI']),
    guestGstin: optionalGstin,
});
/* ---------------- housekeeping ---------------- */
export const HOUSEKEEPING_STATUSES = ['PENDING', 'IN_PROGRESS', 'DONE', 'BLOCKED'];
export const createHousekeepingTaskSchema = z.object({
    roomId: z.string().uuid(),
    type: z.enum(['DEPARTURE_CLEAN', 'STAYOVER_CLEAN', 'DEEP_CLEAN', 'INSPECTION', 'MAINTENANCE']),
    assignedToId: z.string().uuid().optional(),
    dueDate: dateString.optional(),
    note: noteText,
});
export const updateHousekeepingTaskSchema = z.object({
    status: z.enum(HOUSEKEEPING_STATUSES).optional(),
    assignedToId: z.string().uuid().nullable().optional(),
    note: noteText,
});
/* ---------------- people ---------------- */
/**
 * The fields, before any cross-field rule.
 *
 * Kept separate because `.partial()` cannot be used on a refined schema, and
 * an edit has to be free to send one field at a time.
 */
const employeeFields = z.object({
    code: z.string().trim().toUpperCase().max(12).optional(),
    name: shortText,
    role: shortText,
    department: z.enum(['FRONT_OFFICE', 'KITCHEN', 'FNB', 'HOUSEKEEPING', 'STORES', 'ADMIN']),
    dateOfJoining: dateString,
    wageType: z.enum(['MONTHLY', 'DAILY']),
    monthlyGross: rupees.default(0),
    dailyWage: rupees.default(0),
    pfEnrolled: z.boolean().default(true),
    esiEnrolled: z.boolean().default(true),
    phone: optionalPhone,
    bankName: shortText.optional(),
    bankAccount: z.string().trim().max(24).optional().or(z.literal('')),
    ifsc: z
        .string()
        .trim()
        .toUpperCase()
        // Four letters, a zero, then six more. Saying so beats "not valid": the
        // usual mistake is a letter where the zero belongs - ICICI000736 for
        // ICIC0000736 - and nobody spots that from a rejection alone.
        .regex(/^[A-Z]{4}0[A-Z0-9]{6}$/, 'An IFSC is 4 letters, then a zero, then 6 more — like ICIC0000736')
        .optional()
        .or(z.literal('')),
    advanceBalance: rupees.default(0),
});
/*
 * Somebody has to be paid something.
 *
 * Both figures default to zero, and payroll pro-rates from whichever one the
 * wage type points at - so a monthly employee saved at zero gets a zero payslip
 * every month, quietly, until somebody notices at the bank.
 */
const paidSomething = (schema) => schema
    .refine((e) => e.wageType !== 'MONTHLY' || e.monthlyGross === undefined || e.monthlyGross > 0, {
    path: ['monthlyGross'],
    message: 'A monthly employee needs a salary',
})
    .refine((e) => e.wageType !== 'DAILY' || e.dailyWage === undefined || e.dailyWage > 0, {
    path: ['dailyWage'],
    message: 'A daily-wage employee needs a daily rate',
});
export const createEmployeeSchema = paidSomething(employeeFields);
export const updateEmployeeSchema = paidSomething(employeeFields.partial().extend({ isActive: z.boolean().optional() }));
export const markAttendanceSchema = z.object({
    date: dateString,
    entries: z
        .array(z.object({
        employeeId: z.string().uuid(),
        status: z.enum(['PRESENT', 'HALF_DAY', 'ABSENT', 'PAID_LEAVE', 'WEEKLY_OFF']),
        otHours: z.number().min(0).max(12).default(0),
    }))
        .min(1)
        .max(500),
});
export const payrollPeriodSchema = z.object({ period: periodString });
/* ---------------- reporting ---------------- */
export const reportRangeSchema = z.object({
    from: dateString.optional(),
    to: dateString.optional(),
    days: z.coerce.number().int().min(1).max(365).optional(),
});
export const gstExportSchema = z.object({
    period: periodString,
    format: z.enum(['json', 'csv']).default('json'),
    section: z.enum(['B2B', 'B2C', 'HSN', 'ALL']).default('ALL'),
});
export const einvoicePayloadSchema = z.object({
    invoiceId: z.string().uuid(),
});
/* ---------------- online and aggregator orders ---------------- */
export const orderChannelEnum = z.enum(ORDER_CHANNELS);
export const fulfilmentStatusEnum = z.enum(FULFILMENT_STATUSES);
/** A channel an order can actually arrive on. IN_HOUSE is rung up at the till. */
export const onlineChannelEnum = z.enum(['SWIGGY', 'ZOMATO', 'PHONE', 'WEB']);
export const onlineOrderLineSchema = z.object({
    menuItemId: z.string().uuid(),
    quantity,
    note: noteText,
});
/**
 * An order arriving from outside the building.
 *
 * `externalRef` is the aggregator's own order number. It is what a customer
 * quotes on the phone and what makes a retried webhook land on the order it
 * already created rather than a second copy of it.
 */
export const createOnlineOrderSchema = z.object({
    channel: onlineChannelEnum,
    externalRef: z.string().trim().max(60).optional().or(z.literal('')),
    customerName: shortText,
    customerPhone: z.union([optionalPhone, z.literal('')]).optional(),
    deliveryAddress: z.string().trim().max(240).optional().or(z.literal('')),
    /** Blank falls back to the rate configured for the channel. */
    commissionPct: percent.optional(),
    placedAt: z.string().datetime().optional(),
    promisedAt: z.string().datetime().optional(),
    note: noteText,
    lines: z.array(onlineOrderLineSchema).min(1, 'An order needs at least one item').max(60),
});
export const advanceFulfilmentSchema = z.object({
    status: fulfilmentStatusEnum,
    note: noteText,
    /**
     * How a delivered order was paid.
     *
     * Only read on the move to DELIVERED, which is where the invoice is raised.
     * Left out, an aggregator order books as ONLINE - they collected it - and
     * anything else as cash.
     */
    paymentMode: paymentModeEnum.optional(),
});
/** What a diner sends from the hotel's own QR menu. No account, no channel. */
export const guestOrderSchema = z.object({
    customerName: shortText,
    customerPhone: z.union([optionalPhone, z.literal('')]).optional(),
    table: z.string().trim().max(16).optional().or(z.literal('')),
    note: noteText,
    lines: z
        .array(z.object({ menuItemId: z.string().uuid(), quantity: z.number().int().min(1).max(30) }))
        .min(1, 'Add something to the order first')
        .max(40),
});
//# sourceMappingURL=operations.js.map